import React, { useState, useContext, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUser,
  faLock,
  faArrowLeft,
  faEnvelope,
  faEye,
  faEyeSlash,
  faSyncAlt,
} from '@fortawesome/free-solid-svg-icons';
import 'bootstrap/dist/css/bootstrap.min.css';
import '../css/login.css';
import Landing from '../images/login.png';
// import Landing from '../images/jk_new.png';

import logo from '../images/logo.png';
import { BaseUrl } from '../Services/Api';
import { UserContext } from '../context/UserContext';

const Login = () => {
  const navigate = useNavigate();
  const { refreshUser } = useContext(UserContext);

  const [formData, setFormData] = useState({
    username: '',
    password: '',
    email: '',
    captchaInput: '',
  });
  useEffect(() => {
    document.title = 'Login';
  }, []);
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [forgotPassword, setForgotPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);

  // Captcha state
  const [captchaText, setCaptchaText] = useState('');
  const canvasRef = useRef(null);

  // Generate random text for captcha
  const generateCaptchaText = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghjkmnpqrstuvwxyz23456789';
    let text = '';
    for (let i = 0; i < 6; i++) {
      text += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return text;
  };

  // Draw captcha image on canvas
  const drawCaptcha = (text) => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    const width = 140;
    const height = 40;

    canvas.width = width;
    canvas.height = height;

    // Clear previous canvas
    ctx.clearRect(0, 0, width, height);

    // Background
    ctx.fillStyle = '#f2f2f2';
    ctx.fillRect(0, 0, width, height);

    // Random lines
    for (let i = 0; i < 5; i++) {
      ctx.strokeStyle = `rgba(${Math.random()*255}, ${Math.random()*255}, ${Math.random()*255}, 0.6)`;
      ctx.beginPath();
      ctx.moveTo(Math.random() * width, Math.random() * height);
      ctx.lineTo(Math.random() * width, Math.random() * height);
      ctx.stroke();
    }

    // Draw captcha letters
    ctx.font = 'bold 24px sans-serif';
    ctx.fillStyle = '#333';
    const spacing = 20;
    for (let i = 0; i < text.length; i++) {
      const x = 10 + i * spacing;
      const y = 25 + Math.random() * 5;
      ctx.save();
      ctx.translate(x, y);
      const angle = (Math.random() - 0.5) * 0.6;
      ctx.rotate(angle);
      ctx.fillText(text[i], 0, 0);
      ctx.restore();
    }

    // Random dots
    for (let i = 0; i < 30; i++) {
      ctx.fillStyle = `rgba(${Math.random()*255}, ${Math.random()*255}, ${Math.random()*255}, 0.6)`;
      ctx.beginPath();
      ctx.arc(Math.random() * width, Math.random() * height, 1, 0, 2 * Math.PI);
      ctx.fill();
    }
  };

  const generateCaptcha = () => {
    const text = generateCaptchaText();
    setCaptchaText(text);
    drawCaptcha(text);
  };

  // FIRST LOAD
  useEffect(() => {
    generateCaptcha();
  }, []);

  // FIX: regenerate captcha when returning from forgot password
  useEffect(() => {
    if (!forgotPassword) {
      setTimeout(() => {
        generateCaptcha();
      }, 100); // allow DOM to render first
    }
  }, [forgotPassword]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors(prev => ({ ...prev, [name]: '' }));
    if (apiError) setApiError('');
  };

  const validateLogin = () => {
    const newErrors = {};
    if (!formData.username.trim()) newErrors.username = 'Username required';
    if (!formData.password) newErrors.password = 'Password required';
    else if (formData.password.length < 6) newErrors.password = 'Minimum 6 characters';
    if (!formData.captchaInput.trim()) newErrors.captchaInput = 'Captcha required';
    else if (formData.captchaInput.toLowerCase() !== captchaText.toLowerCase())
      newErrors.captchaInput = 'Captcha incorrect';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateEmail = () => {
    const newErrors = {};
    if (!formData.email.trim()) newErrors.email = 'Email required';
    else if (!/\S+@\S+\.\S+/.test(formData.email)) newErrors.email = 'Invalid email';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (forgotPassword) {
      if (!validateEmail()) return;
      setIsSubmitting(true);
      setApiError('');
      try {
        const response = await fetch(`${BaseUrl}/reset-link`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ email: formData.email }),
        });
        const responseData = await response.json();

        if (responseData.status=='success') {
          toast.success(responseData.message || 'Password reset link sent!');
          setForgotPassword(false);
        } else {        
          toast.error(responseData.message || 'Failed to send reset email.');
          setApiError(responseData.message || 'Failed to send reset email.');
        }
      } catch (error) {
        setApiError('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    } else {
      if (!validateLogin()) return;
      setIsSubmitting(true);
      setApiError('');
      try {
        const response = await fetch(`${BaseUrl}/login`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            username: formData.username,
            password: formData.password,
          }),
          credentials: 'include',
        });

        const responseData = await response.json();

        if (response.ok && responseData.status) {
          // toast.success("Login successful!");
          await refreshUser();
          navigate("/");
        } else {
          if (responseData.errors) {
            const apiErrors = {};
            Object.keys(responseData.errors).forEach(key => {
              apiErrors[key] = responseData.errors[key][0];
            });
            setErrors(apiErrors);
          } else {
            setApiError(responseData.message || 'Login failed');
          }
        }
      } catch (error) {
        setApiError('Network error. Please try again.');
      } finally {
        setIsSubmitting(false);
      }
    }
  };

  return (
    <div className="login-container">
      <ToastContainer position="top-right" autoClose={3000} />
      <div className="login-wrapper">
        <div className="login-grid">

          {/* LEFT IMAGE SIDE */}
          <div className="registration-image-container">
            <img src={Landing} alt="Registration" className="registration-image" />
            <div className="image-overlay">
              <h2>Join Our LSC Community</h2>
              <p>Create an account to access exclusive features</p>
            </div>
          </div>

          {/* LOGIN FORM */}
          <div className="login-form-container">
            <div className="form-header">
              <button onClick={() => navigate('/')} className="btn-back">
                <FontAwesomeIcon icon={faArrowLeft} className="me-2" />
                Home
              </button>
              <div className='logo-container'>
                <img src={logo} alt="Company Logo" className="branding-logo1" />
              </div>
              <h1 className="branding-title">Online Certificate Programmes</h1>
            </div>

            <form onSubmit={handleSubmit} className="login-form">
              {apiError && <div className="alert alert-danger">{apiError}</div>}

              {/* FORGOT PASSWORD SECTION */}
              {forgotPassword ? (
                <>
                  <h4 className='forgot-title'>Forgot password</h4>
                  <div className="form-group">
                    <label className="form-label">Email</label>
                    <div className="input-with-icon">
                      <FontAwesomeIcon icon={faEnvelope} className="input-icon" />
                      <input
                        type="email"
                        className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="Enter your email"
                      />
                    </div>
                    {errors.email && (
                      <div className="invalid-feedback d-block">{errors.email}</div>
                    )}
                  </div>
                  <button type="submit" className="btn-login" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : 'Send Reset Link'}
                  </button>
                  <div className="form-footer mt-3">
                    <button
                      type="button"
                      className="btn-link"
                      onClick={() => setForgotPassword(false)}
                    >
                      Back to Login
                    </button>
                  </div>
                </>
              ) : (
                <>
                  {/* LOGIN FIELDS */}
                  <div className="form-group">
                    <label className="form-label">Username</label>
                    <div className="input-with-icon">
                      <FontAwesomeIcon icon={faUser} className="input-icon" />
                      <input
                        type="text"
                        className={`form-control ${errors.username ? 'is-invalid' : ''}`}
                        name="username"
                        value={formData.username}
                        onChange={handleChange}
                        placeholder="Enter your username"
                      />
                    </div>
                    {errors.username && (
                      <div className="invalid-feedback d-block">{errors.username}</div>
                    )}
                  </div>

                  <div className="form-group">
                    <label className="form-label">Password</label>
                    <div className="input-with-icon">
                      <FontAwesomeIcon icon={faLock} className="input-icon" />
                      <input
                        type={showPassword ? 'text' : 'password'}
                        className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                        name="password"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="Enter your password"
                      />
                      <FontAwesomeIcon
                        icon={showPassword ? faEyeSlash : faEye}
                        className="password-toggle-icon"
                        onClick={() => setShowPassword(!showPassword)}
                      />
                    </div>
                    {errors.password && (
                      <div className="invalid-feedback d-block">{errors.password}</div>
                    )}
                  </div>

                  {/* CAPTCHA */}
                  <div className="form-group">
                    <label className="form-label">Captcha</label>
                    <div className="captcha-container">
                      <canvas ref={canvasRef} className="captcha-canvas" />
                      <FontAwesomeIcon
                        icon={faSyncAlt}
                        className="captcha-refresh"
                        title="Refresh Captcha"
                        onClick={generateCaptcha}
                      />
                    </div>

                    <input
                      type="text"
                      className={`form-control mt-2 ${errors.captchaInput ? 'is-invalid' : ''}`}
                      name="captchaInput"
                      value={formData.captchaInput}
                      onChange={handleChange}
                      placeholder="Enter captcha text"
                    />
                    {errors.captchaInput && (
                      <div className="invalid-feedback d-block">{errors.captchaInput}</div>
                    )}
                  </div>

                  <div className="form-options">
                    <button
                      type="button"
                      className="btn-forgot"
                      onClick={() => setForgotPassword(true)}
                    >
                      Forgot password?
                    </button>
                  </div>

                  <button type="submit" className="btn-login" disabled={isSubmitting}>
                    {isSubmitting ? (
                      <>
                        <span
                          className="spinner-border spinner-border-sm"
                          role="status"
                          aria-hidden="true"
                        ></span>
                        <span className="ms-2">Verifying Account...</span>
                      </>
                    ) : (
                      'Login'
                    )}
                  </button>

                  <div className="form-footer">
                    <p>
                      Don't have an account?{' '}
                      <button
                        type="button"
                        className="btn-link"
                        onClick={() => navigate('/register')}
                      >
                        Sign Up
                      </button>
                    </p>
                  </div>
                </>
              )}
            </form>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Login;
